<script setup lang="ts">
import {NotificationSequence} from "@/notification-sequences/interfaces/NotificationSequence";
import BaseButton from "@/core/components/base/BaseButton.vue";
import NewFeatureTag from "@/core/components/tags/NewFeatureTag.vue";
import NotificationSequenceSteps
    from "@/notification-sequences/components/notification-sequence-steps/NotificationSequenceSteps.vue";
import {Switch} from "@/core/components/ui/switch";
import {Label} from "@/core/components/ui/label";
import {ArrowRight} from "@lucide/vue";
import {computed, ref, watch, type HTMLAttributes} from "vue";
import {notificationSequenceApi} from "@/container";
import {pluralize} from "@/core/filters/Pluralize";
import {cn} from "@/lib/utils";

const props = withDefaults(defineProps<{
    sequence: NotificationSequence
    color?: string
    /** Show the pharmacy-level enable switch (listing pages). */
    toggleable?: boolean
    ctaLabel?: string
    newTag?: boolean
    class?: HTMLAttributes['class']
}>(), {
    color: undefined,
    toggleable: true,
    ctaLabel: 'Découvrir la séquence',
    newTag: false,
    class: undefined
});

const emits = defineEmits<{
    navigate: [sequence: NotificationSequence]
}>();

const enabled = ref<boolean | undefined>(props.sequence.is_enabled ?? false);

watch(() => props.sequence, (newValue) => {
    enabled.value = newValue.is_enabled;
});

/** The description line, appending the scheduled message count. */
const subtitle = computed(() => {
    const count = props.sequence.messages?.length ?? 0;
    const countLabel = count > 0
        ? `${count} ${pluralize('message programmé', count)}`
        : null;

    return [props.sequence.description, countLabel].filter(Boolean).join(' — ');
});

/**
 * Toggle the enabled state of the notification sequence
 *
 * @param value
 */
const toggleEnabled = async (value: boolean | null) => {
    if (value === null) {
        return;
    }

    enabled.value = value;
    try {
        await notificationSequenceApi()
            .toggleEnabled(props.sequence.id, value)
            .then((response) => {
                enabled.value = response.enabled;
            });
    } catch {
        enabled.value = props.sequence.is_enabled;
    }
};
</script>

<template>
  <div :class="cn('bg-white rounded-2xl px-6 py-5 flex flex-col gap-4 h-full', props.class)">
    <div class="flex items-center gap-4">
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2.5 flex-wrap">
          <h2 class="text-gray-dark">
            {{ sequence.name }}
          </h2>
          <NewFeatureTag v-if="newTag" class="shrink-0" />
        </div>
        <p v-if="subtitle" class="text-sm text-muted-foreground mt-0.5 line-clamp-2">
          {{ subtitle }}
        </p>
      </div>
      <div v-if="toggleable" class="flex items-center gap-2 shrink-0">
        <Label class="text-sm hidden md:block">{{ enabled ? 'Activée' : 'Désactivée' }}</Label>
        <Switch :model-value="enabled" @update:model-value="toggleEnabled" />
      </div>
      <BaseButton
        v-else
        :color="color ?? 'primary'"
        density="comfortable"
        class="shrink-0"
        @click="emits('navigate', sequence)"
      >
        {{ ctaLabel }}
        <ArrowRight class="ml-2 w-4" />
      </BaseButton>
    </div>
    <NotificationSequenceSteps
      v-if="sequence.messages?.length"
      :messages="sequence.messages"
    />
    <BaseButton
      v-if="toggleable"
      :color="color ?? 'primary'"
      density="comfortable"
      class="self-end mt-auto"
      @click="emits('navigate', sequence)"
    >
      {{ ctaLabel }}
      <ArrowRight class="ml-2 w-4" />
    </BaseButton>
  </div>
</template>

<style scoped>

</style>
